import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { LAYOUT_LABELS } from '@/admin/sitecontent/conditions';
import { OnboardingSlideForm } from '@/admin/sitecontent/OnboardingSlideForm';
import { HeaderRow, StatusMessage } from '@/admin/sitecontent/StatusMessage';
import buttonStyles from '@/admin/styles/button.module.css';
import { Badge } from '@/admin/ui/Badge';
import { DetailPage } from '@/admin/ui/Editor';
import { useEditMode, validateEditSearch } from '@/admin/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '@/admin/ui/FieldDisplay';
import { BackLink } from '@/admin/ui/NavLinks';
import { PageTitle } from '@/admin/ui/Page';
import { RichContent } from '@/admin/ui/RichContent';
import { FieldGrid, Section, SectionTitle } from '@/admin/ui/Section';
import api from '@/api';

export const Route = createFileRoute(
  '/admin/onboarding-slides/$onboardingId/$slideIndex',
)({
  staticData: { minTier: 'ADMIN' },
  validateSearch: validateEditSearch,
  component: SlideDetail,
});

function SlideDetail() {
  const { t } = useTranslation();
  const { onboardingId, slideIndex } = Route.useParams();
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);
  const { data, isLoading, error } = api.getOnboardingSlide.useQuery({
    path: {
      onboarding_id: Number(onboardingId),
      slide_index: Number(slideIndex),
    },
  });

  if (isLoading)
    return (
      <StatusMessage>{t('admin.onboardingSlides.loadingOne')}</StatusMessage>
    );
  if (error)
    return (
      <StatusMessage $error>
        {t('admin.onboardingSlides.errorLoadingOne')}
      </StatusMessage>
    );
  if (!data)
    return (
      <StatusMessage $error>
        {t('admin.onboardingSlides.notFound')}
      </StatusMessage>
    );

  return (
    <DetailPage>
      <BackLink>
        <Link
          to="/admin/onboarding-slides/"
          search={{ onboarding: Number(onboardingId) }}
        >
          {t('admin.onboardingSlides.backToSlides')}
        </Link>
      </BackLink>
      <HeaderRow>
        <PageTitle>
          {t('admin.onboardingSlides.slideN', {
            index: data.slide_index,
            interpolation: { escapeValue: false },
          })}
        </PageTitle>
        {!editing && (
          <Button className={buttonStyles.Button} onClick={startEditing}>
            {t('admin.common.edit')}
          </Button>
        )}
      </HeaderRow>

      {editing ? (
        <OnboardingSlideForm
          key={`${onboardingId}:${slideIndex}`}
          onboardingId={Number(onboardingId)}
          slide={data}
          onSaved={stopEditing}
          onCancel={stopEditing}
        />
      ) : (
        <>
          <Section>
            <SectionTitle>{t('admin.common.details')}</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>
                  {t('admin.onboardingSlides.slideIndex')}
                </FieldLabel>
                <FieldValue>{data.slide_index}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.onboardingSlides.layout')}</FieldLabel>
                <FieldValue>{t(LAYOUT_LABELS[data.layout])}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.onboardingSlides.enabled')}</FieldLabel>
                <FieldValue>
                  <Badge $variant={data.enabled ? 'success' : 'error'}>
                    {data.enabled
                      ? t('admin.common.yes')
                      : t('admin.common.no')}
                  </Badge>
                </FieldValue>
              </Field>
              <Field>
                <FieldLabel>
                  {t('admin.onboardingSlides.buttonText')}
                </FieldLabel>
                <FieldValue>{data.next_step_button_text || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>{t('admin.common.title')}</FieldLabel>
                <FieldValue>{data.title || '—'}</FieldValue>
              </Field>
            </FieldGrid>
          </Section>
          <Section>
            <SectionTitle>{t('admin.onboardingSlides.content')}</SectionTitle>
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: admin view of backend-authored slide HTML */}
            <RichContent dangerouslySetInnerHTML={{ __html: data.content }} />
          </Section>
          {data.profile_fields.length > 0 && (
            <Section>
              <SectionTitle>
                {t('admin.onboardingSlides.xprofileFields')}
              </SectionTitle>
              <ul>
                {data.profile_fields.map((field) => (
                  <li key={field.id}>{field.name}</li>
                ))}
              </ul>
            </Section>
          )}
        </>
      )}
    </DetailPage>
  );
}
